import React, { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCategories } from '../../contexts/CategoriesContext';
import { useSlider } from '../../contexts/SliderContext';

const MobileCategoryNav = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { currentSliderTextColor } = useSlider();
  const { i18n, t } = useTranslation();
  const { getParentCategories } = useCategories();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const sliderRef = useRef(null);

  const categories = getParentCategories();

  useEffect(() => {
    checkArrows();
    window.addEventListener('resize', checkArrows);
    return () => window.removeEventListener('resize', checkArrows);
  }, [categories]);

  const checkArrows = () => {
    if (!sliderRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scroll = (direction) => {
    if (!sliderRef.current) return;
    
    const scrollAmount = 200;
    const newScrollLeft = direction === 'left' 
      ? sliderRef.current.scrollLeft - scrollAmount
      : sliderRef.current.scrollLeft + scrollAmount;
    
    sliderRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
    
    setTimeout(checkArrows, 300);
  };

  if (categories.length === 0) return null;

  // Get current language
  const currentLang = i18n.language || 'es';

  const useWhiteText = isHomePage && currentSliderTextColor === 'white';

  return (
    <div className={`lg:hidden border-b sticky top-16 z-40 ${
      useWhiteText
        ? 'border-transparent bg-transparent' 
        : 'border-transparent bg-transparent'
    }`}>
      <div className="container mx-auto px-4 relative py-3">
        {/* Mobile: Swipeable slider */}
        <div className="relative">
          {/* Left Arrow - Hidden, users can swipe */}
          {false && showLeftArrow && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm shadow-lg rounded-full p-2 hover:bg-white transition-all"
            >
              <ChevronLeft className="h-4 w-4 text-[#2C2420]" />
            </button>
          )}

          {/* Scrollable container */}
          <div
            ref={sliderRef}
            onScroll={checkArrows}
            className="flex gap-6 overflow-x-auto scrollbar-hide py-4 px-2 scroll-smooth snap-x snap-mandatory"
            style={{
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {/* "Todos" — quick link to all products */}
            <Link
              key="__all__"
              to="/shop"
              data-testid="mobile-nav-all-products"
              className="flex-shrink-0 snap-start"
            >
              <div className={`text-sm font-semibold transition-colors whitespace-nowrap px-2 py-1 rounded-full drop-shadow-lg ${
                useWhiteText
                  ? 'text-white hover:text-gray-200'
                  : 'text-[#2C2420] hover:text-[#be1f1a]'
              }`}>
                {t('nav.all', 'Todos')}
              </div>
            </Link>
            {categories.map((category) => {
              // Extract name from versioned schema or old schema
              let displayName = category.slug;
              if (category.versions && category.versions.length > 0) {
                const firstVersion = category.versions.find(v => v.name) || category.versions[0];
                displayName = firstVersion?.name || category.slug;
              } else if (category.name) {
                const currentLang = i18n.language || 'es';
                displayName = category.name?.[currentLang] || category.name?.es || category.name;
              }
              
              return (
                <Link
                  key={category.slug}
                  to={`/shop/${category.slug}`}
                  className="flex-shrink-0 snap-start"
                >
                  <div className={`text-sm font-medium transition-colors whitespace-nowrap px-2 py-1 rounded-full drop-shadow-lg ${
                    useWhiteText
                      ? 'text-white hover:text-gray-200' 
                      : 'text-[#2C2420] hover:text-[#be1f1a]'
                  }`}>
                    {displayName}
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Right Arrow - Hidden, users can swipe */}
          {false && showRightArrow && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm shadow-lg rounded-full p-2 hover:bg-white transition-all"
            >
              <ChevronRight className="h-4 w-4 text-[#2C2420]" />
            </button>
          )}
        </div>
      </div>

      {/* Hide scrollbar globally */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default MobileCategoryNav;
